/**
 * GameMenu - Norse-themed expandable dock/drawer
 * 
 * Contains tabs for:
 * - Inventory (empty functional grid shell)
 * - Map (full Midgard map with waypoint selection)
 * - Navigation (fast travel, if unlocked)
 * - Character (stats placeholder)
 * - Settings (link to game settings)
 */

import { useState, useCallback } from 'react';
import { 
  Package, Map, Navigation, User, Settings, X, ChevronUp, ChevronDown,
  MapPin, Trash2
} from 'lucide-react';

// Inventory grid configuration
const INVENTORY_ROWS = 4;
const INVENTORY_COLS = 6;

// Known map locations for waypoint selection
const MAP_LOCATIONS = [
  { id: 'thornhaven', name: 'Thornhaven', position: { x: 0, z: 0 }, unlocked: true },
  { id: 'standing-stones', name: 'Standing Stones', position: { x: -15, z: -190 }, unlocked: true },
  { id: 'watchtower', name: 'Ruined Watchtower', position: { x: 18, z: -320 }, unlocked: true },
  { id: 'bridge', name: 'Stone Bridge', position: { x: 0, z: -430 }, unlocked: true },
  { id: 'camp', name: 'Abandoned Camp', position: { x: -12, z: -495 }, unlocked: true },
  { id: 'frostholm', name: 'Frostholm', position: { x: 0, z: -660 }, unlocked: true },
];

/**
 * Inventory Tab - Empty functional grid
 */
function InventoryTab() {
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  const slots = Array(INVENTORY_ROWS * INVENTORY_COLS).fill(null);
  
  return (
    <div className="p-4">
      <h3 className="text-amber-200 font-semibold mb-3">Inventory</h3>
      <div 
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${INVENTORY_COLS}, minmax(0, 1fr))` }}
      >
        {slots.map((_, index) => (
          <button
            key={index}
            onClick={() => setSelectedSlot(index === selectedSlot ? null : index)}
            className={`
              aspect-square rounded border transition-all
              ${selectedSlot === index 
                ? 'border-amber-400 bg-amber-900/40' 
                : 'border-amber-900/50 bg-black/30 hover:border-amber-700/50'
              }
            `}
            data-testid={`inventory-slot-${index}`}
          >
            {/* Empty slot - items would render here */}
          </button>
        ))}
      </div>
      <p className="text-amber-600/60 text-xs mt-3 text-center italic">
        Inventory system coming soon
      </p>
    </div>
  );
}

/**
 * Map Tab - Full world map with waypoint selection
 */
function MapTab({ currentWaypoint, onSetWaypoint, onClearWaypoint, playerPosition }) {
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  const handleSetWaypoint = useCallback((location) => {
    onSetWaypoint?.(location.position);
    setSelectedLocation(location.id);
  }, [onSetWaypoint]);
  
  const handleClearWaypoint = useCallback(() => {
    onClearWaypoint?.();
    setSelectedLocation(null);
  }, [onClearWaypoint]);
  
  // Calculate distance to each location
  const locationsWithDistance = MAP_LOCATIONS.map(loc => ({
    ...loc,
    distance: Math.round(Math.sqrt(
      Math.pow(loc.position.x - (playerPosition?.x || 0), 2) +
      Math.pow(loc.position.z - (playerPosition?.z || 0), 2)
    )),
  })).sort((a, b) => a.distance - b.distance);
  
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-amber-200 font-semibold">World Map</h3>
        {currentWaypoint && (
          <button
            onClick={handleClearWaypoint}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-900/40 border border-red-700/50 text-red-300 hover:bg-red-900/60"
          >
            <Trash2 className="w-3 h-3" />
            Clear Waypoint
          </button>
        )}
      </div>
      
      {/* Simple map visualization */}
      <div className="relative w-full h-48 rounded-lg bg-gradient-to-b from-amber-950/40 to-emerald-950/40 border border-amber-900/50 mb-3 overflow-hidden">
        {/* Road line */}
        <div 
          className="absolute left-1/2 top-0 bottom-0 w-1 bg-amber-800/40 -translate-x-1/2"
        />
        
        {/* Location markers */}
        {locationsWithDistance.map((loc, index) => {
          // Map Z position to vertical position (z=0 at top, z=-700 at bottom)
          const yPercent = Math.max(5, Math.min(95, ((loc.position.z + 50) / -750) * 100 + 5));
          const xPercent = 50 + (loc.position.x / 50) * 20; // Slight horizontal offset
          
          const isSelected = selectedLocation === loc.id;
          const isWaypoint = currentWaypoint && 
            currentWaypoint.x === loc.position.x && 
            currentWaypoint.z === loc.position.z;
          
          return (
            <button
              key={loc.id}
              onClick={() => handleSetWaypoint(loc)}
              className={`
                absolute transform -translate-x-1/2 -translate-y-1/2 
                w-4 h-4 rounded-full border-2 transition-all
                ${isWaypoint 
                  ? 'bg-emerald-500 border-emerald-300 scale-125' 
                  : isSelected 
                    ? 'bg-amber-500 border-amber-300'
                    : 'bg-amber-700 border-amber-600 hover:bg-amber-600'
                }
              `}
              style={{ left: `${xPercent}%`, top: `${yPercent}%` }}
              title={loc.name}
            />
          );
        })}
        
        {/* Player position indicator */}
        {playerPosition && (
          <div
            className="absolute w-3 h-3 rounded-full bg-blue-400 border-2 border-blue-200 transform -translate-x-1/2 -translate-y-1/2 z-10"
            style={{
              left: `${50 + (playerPosition.x / 50) * 20}%`,
              top: `${Math.max(5, Math.min(95, ((playerPosition.z + 50) / -750) * 100 + 5))}%`,
            }}
          />
        )}
      </div>
      
      {/* Location list */}
      <div className="space-y-1 max-h-32 overflow-y-auto pb-4">
        {locationsWithDistance.map(loc => {
          const isWaypoint = currentWaypoint && 
            currentWaypoint.x === loc.position.x && 
            currentWaypoint.z === loc.position.z;
          
          return (
            <button
              key={loc.id}
              onClick={() => handleSetWaypoint(loc)}
              className={`
                w-full flex items-center justify-between px-3 py-2 rounded text-sm
                ${isWaypoint 
                  ? 'bg-emerald-900/40 border border-emerald-700/50' 
                  : 'bg-black/30 border border-amber-900/30 hover:border-amber-700/50'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <MapPin className={`w-3 h-3 ${isWaypoint ? 'text-emerald-400' : 'text-amber-600'}`} />
                <span className={isWaypoint ? 'text-emerald-200' : 'text-amber-200'}>{loc.name}</span>
              </div>
              <span className="text-amber-600/60 text-xs">{loc.distance}m</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Navigation Tab - Fast travel points
 */
function NavigationTab({ onFastTravel }) {
  return (
    <div className="p-4">
      <h3 className="text-amber-200 font-semibold mb-3">Fast Travel</h3>
      <p className="text-amber-600/60 text-sm text-center italic">
        Fast travel unlocks coming soon
      </p>
    </div>
  );
}

/**
 * Character Tab - Stats placeholder
 */
function CharacterTab() {
  return (
    <div className="p-4">
      <h3 className="text-amber-200 font-semibold mb-3">Character</h3>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-amber-600">Level</span>
          <span className="text-amber-200">1</span>
        </div>
        <div className="flex justify-between">
          <span className="text-amber-600">Health</span>
          <span className="text-amber-200">100/100</span>
        </div>
        <div className="flex justify-between">
          <span className="text-amber-600">Stamina</span>
          <span className="text-amber-200">100/100</span>
        </div>
      </div>
      <p className="text-amber-600/60 text-xs mt-3 text-center italic">
        Character progression coming soon
      </p>
    </div>
  );
}

/**
 * Settings Tab - Links to game settings
 */
function SettingsTab({ onOpenSettings }) {
  return (
    <div className="p-4">
      <h3 className="text-amber-200 font-semibold mb-3">Settings</h3>
      <button
        onClick={onOpenSettings}
        className="w-full px-4 py-2 rounded bg-amber-900/40 border border-amber-700/50 text-amber-200 hover:bg-amber-900/60 transition-colors"
      >
        Open Game Settings
      </button>
    </div>
  );
}

// Tab configuration
const TABS = [
  { id: 'inventory', label: 'Inventory', icon: Package },
  { id: 'map', label: 'Map', icon: Map },
  { id: 'navigation', label: 'Travel', icon: Navigation },
  { id: 'character', label: 'Character', icon: User },
  { id: 'settings', label: 'Settings', icon: Settings },
];

/**
 * Main GameMenu Component
 */
export default function GameMenu({
  isOpen,
  onToggle,
  currentWaypoint,
  onSetWaypoint,
  onClearWaypoint,
  onOpenSettings,
  onFastTravel,
  playerPosition,
}) {
  const [activeTab, setActiveTab] = useState('map');
  
  const renderTabContent = () => {
    switch (activeTab) {
      case 'inventory':
        return <InventoryTab />;
      case 'map':
        return (
          <MapTab 
            currentWaypoint={currentWaypoint}
            onSetWaypoint={onSetWaypoint}
            onClearWaypoint={onClearWaypoint}
            playerPosition={playerPosition}
          />
        );
      case 'navigation':
        return <NavigationTab onFastTravel={onFastTravel} />;
      case 'character':
        return <CharacterTab />;
      case 'settings':
        return <SettingsTab onOpenSettings={onOpenSettings} />;
      default:
        return null;
    }
  };
  
  return (
    <>
      {/* Menu Toggle Button */}
      <button
        onClick={onToggle}
        className={`
          fixed left-1/2 -translate-x-1/2 z-40 
          px-4 py-2 rounded-t-lg 
          bg-gradient-to-b from-amber-900/90 to-amber-950/90 
          border border-b-0 border-amber-700/50 
          backdrop-blur-sm shadow-lg
          transition-all duration-300
          ${isOpen ? 'bottom-[340px]' : 'bottom-0'}
        `}
        style={{ paddingBottom: isOpen ? '8px' : 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}
        data-testid="menu-toggle"
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-amber-400" />
          ) : (
            <ChevronUp className="w-4 h-4 text-amber-400" />
          )}
          <span className="text-amber-200 text-sm font-medium">Menu</span>
        </div>
      </button>
      
      {/* Menu Drawer */}
      <div 
        className={`
          fixed left-0 right-0 bottom-0 z-30
          bg-gradient-to-t from-black/95 via-amber-950/90 to-amber-900/80
          border-t border-amber-700/50 backdrop-blur-md
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-y-0' : 'translate-y-full'}
        `}
        style={{ 
          height: '340px',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        data-testid="game-menu"
      >
        {/* Tab Bar */}
        <div className="flex border-b border-amber-900/50">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 flex flex-col items-center gap-1 py-2 px-1 transition-colors
                  ${isActive 
                    ? 'bg-amber-900/40 border-b-2 border-amber-400' 
                    : 'hover:bg-amber-900/20'
                  }
                `}
                data-testid={`menu-tab-${tab.id}`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-amber-400' : 'text-amber-600'}`} />
                <span className={`text-[10px] ${isActive ? 'text-amber-200' : 'text-amber-600'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
        
        {/* Tab Content */}
        <div className="h-[calc(100%-60px)] overflow-y-auto">
          {renderTabContent()}
        </div>
      </div>
      
      {/* Backdrop when open */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-20"
          onClick={onToggle}
        />
      )}
    </>
  );
}
